import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
} from '@mui/material'
import {
  Description as DocumentIcon,
  Gavel as CaseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  setSelectedCitation,
  setDocument,
  setDocumentSourceUrl,
  setLoadingDocument,
} from '../../../redux/actions/documentAction'
import { backendClient } from '../../../api/backend'

/**
 * Citations/Sources display component
 *
 * Groups retrieved documents by document_id and shows sections within each group
 */
const Citations = ({ sources, currentLang, onCitationClick }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [expandedDocId, setExpandedDocId] = useState(null)
  const [loadingSectionKey, setLoadingSectionKey] = useState(null)
  const document = useSelector(state => state.document?.document)
  const selectedCitation = useSelector(state => state.document?.selectedCitation)

  // Group sources by document_id
  const groupedSources = useMemo(() => {
    if (!sources || sources.length === 0) return []

    const groups = {}
    const order = []

    sources.forEach((source) => {
      const metadata = source.metadata || {}
      const docId = source.document_id || metadata.document_id || `ungrouped-${Math.random()}`

      if (!groups[docId]) {
        groups[docId] = {
          docId,
          metadata, // use first source's metadata as representative
          sources: [],
        }
        order.push(docId)
      }
      groups[docId].sources.push(source)
    })

    return order.map(id => groups[id])
  }, [sources])

  const handleExpandDoc = (docId) => {
    setExpandedDocId(expandedDocId === docId ? null : docId)
  }

  const handleOpenDocument = async (source, sectionKey) => {
    const metadata = source.metadata || {}
    const documentId = metadata.document_id

    if (!documentId) {
      console.warn('No document ID found in citation')
      return
    }

    // Use composite id to guarantee uniqueness across sections of the same doc
    const sectionId = source.id ? `${source.id}` : `${documentId}-${metadata.section_title || Math.random()}`
    const selectedCitationObj = {
      id: sectionId,
      metadata: metadata,
      page_content: source.page_content || source.content
    }

    dispatch(setSelectedCitation(selectedCitationObj))

    if (document?.id === documentId) {
      if (onCitationClick) {
        onCitationClick(source)
      }
      return
    }

    try {
      setLoadingSectionKey(sectionKey)
      dispatch(setLoadingDocument(true))

      const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME
      const lang = metadata.source_language || metadata.language || 'ar'

      let blobName
      const files = metadata.files

      if (files) {
        if (files.html && files.html[lang]) {
          blobName = files.html[lang]
        } else if (files.html && files.html.ar) {
          blobName = files.html.ar
        } else if (files.html) {
          blobName = Object.values(files.html)[0]
        } else if (files.pdf && files.pdf[lang]) {
          blobName = files.pdf[lang]
        } else if (files.pdf && files.pdf.ar) {
          blobName = files.pdf.ar
        } else if (files.pdf) {
          blobName = Object.values(files.pdf)[0]
        }
      }

      if (!blobName) {
        throw new Error('No document path found in metadata')
      }

      const data = await backendClient.getDocumentSas(containerName, blobName)

      if (!data || !data.blob_url_with_sas) {
        throw new Error('Invalid SAS URL response')
      }

      const response = await fetch(data.blob_url_with_sas)
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status}`)
      }

      const url = response.url

      if (!url) {
        throw new Error('No URL received from document fetch')
      }

      dispatch(
        setDocument({
          id: documentId,
          url: url,
          language: lang
        })
      )
      dispatch(setDocumentSourceUrl(url))

      if (onCitationClick) {
        onCitationClick(source)
      }
    } catch (error) {
      console.error('Error fetching or processing the document:', error)
      enqueueSnackbar(t('document_fetch_error'), { variant: 'error' })
    } finally {
      dispatch(setLoadingDocument(false))
      setLoadingSectionKey(null)
    }
  }

  const getSourceIcon = (source) => {
    const metadata = source.metadata || {}
    if (metadata.doc_type === 'case-law') {
      return <CaseIcon fontSize="small" />
    }
    return <DocumentIcon fontSize="small" />
  }

  const getSourceTitle = (source) => {
    const metadata = source.metadata || {}
    if (metadata.doc_type === 'case-law') {
      return metadata.case_number || metadata.subject_matter || t('unknown_document')
    }
    return metadata.title || t('unknown_document')
  }

  const getDocSubtitle = (metadata) => {
    const parts = []

    if (metadata.doc_type === 'law') {
      if (metadata.doc_type_label) {
        parts.push(metadata.doc_type_label)
      } else if (metadata.legislation_type) {
        const type = metadata.legislation_type[currentLang] || metadata.legislation_type['en'] || metadata.legislation_type['ar']
        if (type) parts.push(type)
      }
      if (metadata.doc_number) {
        parts.push(`No. ${metadata.doc_number}`)
      }
      if (metadata.issue_year) {
        parts.push(`${metadata.issue_year}`)
      }
    } else if (metadata.doc_type === 'case-law') {
      if (metadata.court_name) parts.push(metadata.court_name)
      if (metadata.case_type) parts.push(metadata.case_type)
      if (metadata.case_year) parts.push(`${metadata.case_year}`)
    }

    return parts.join(' • ')
  }

  const getSectionLabel = (source) => {
    const metadata = source.metadata || {}
    return metadata.section_title || metadata.chunk_type || ''
  }

  if (!sources || sources.length === 0) return null

  const totalSources = groupedSources.length

  return (
    <Box
      data-tour="chat-citations-panel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <DocumentIcon fontSize="small" />
        {t('sources')} ({totalSources})
      </Typography>

      {groupedSources.map((group) => {
        const { docId, metadata, sources: groupSources } = group
        const isExpanded = expandedDocId === docId
        const hasSingleSection = groupSources.length === 1

        const selectedDocId = selectedCitation?.metadata?.document_id || selectedCitation?.document_id
        const isSelected = selectedCitation && docId === selectedDocId

        return (
          <Paper
            key={docId}
            elevation={0}
            sx={{
              border: isSelected
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              ...(isSelected && {
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              }),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
            }}
          >
            {/* Document Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                cursor: 'pointer',
                bgcolor: isExpanded
                  ? alpha(theme.palette.primary.main, 0.03)
                  : 'transparent',
              }}
              onClick={() => handleExpandDoc(docId)}
            >
              {/* Source Icon */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                }}
              >
                {getSourceIcon(groupSources[0])}
              </Box>

              {/* Title & Subtitle */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getSourceTitle(groupSources[0])}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {getDocSubtitle(metadata)}
                  {!hasSingleSection && ` • ${groupSources.length} ${t('sections', 'sections')}`}
                </Typography>
              </Box>

              {/* Expand Icon */}
              <IconButton size="small">
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Box>

            {/* Expanded Content */}
            <Collapse in={isExpanded}>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1 }}>
                {groupSources.map((source, sIdx) => {
                  const sectionKey = `${docId}-${sIdx}`
                  const sectionLabel = getSectionLabel(source)
                  const sectionCitationId = source.id ? `${source.id}` : `${docId}-${source.metadata?.section_title || sIdx}`
                  const isActiveCitation = selectedCitation?.id === sectionCitationId
                  const isLoading = loadingSectionKey === sectionKey

                  return (
                    <Box
                      key={sectionKey}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        cursor: isLoading ? 'wait' : 'pointer',
                        borderRadius: 1,
                        bgcolor: isActiveCitation
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                      onClick={() => handleOpenDocument(source, sectionKey)}
                    >
                      {isLoading && (
                        <CircularProgress size={14} color="primary" />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          flex: 1,
                          fontWeight: 500,
                          color: isActiveCitation
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        }}
                      >
                        {sectionLabel}
                      </Typography>
                      <OpenIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                    </Box>
                  )
                })}
              </Box>
            </Collapse>
          </Paper>
        )
      })}
    </Box>
  )
}

export default Citations
